import * as THREE from 'three';

/**
 * System for generating trees and bushes in the environment
 */
export class VegetationSystem {
  private scene: THREE.Scene;
  private treeModels: THREE.Group[] = [];
  private bushModels: THREE.Group[] = [];
  private trees: THREE.Group[] = [];
  private bushes: THREE.Group[] = [];
  private boundarySize: number = 300; // Size of the area where vegetation can appear
  private castleRadius: number = 60; // Reduced radius around the castle where vegetation should not appear
  private treeCount: number = 40; // Number of trees to generate
  private bushCount: number = 60; // Number of bushes to generate
  private textureLoader: THREE.TextureLoader;
  
  /**
   * Creates a new VegetationSystem
   * @param scene The scene to add vegetation to
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.textureLoader = new THREE.TextureLoader();
    
    // Create tree and bush models
    this.createTreeModels();
    this.createBushModels();
    
    // Generate vegetation
    this.generateVegetation();
  }
  
  /**
   * Creates different tree models for variety
   */
  private createTreeModels(): void {
    // Create several different tree models for variety
    
    // Pine tree model
    const pineTree = new THREE.Group();
    
    // Tree trunk
    const trunkGeometry = new THREE.CylinderGeometry(0.5, 0.7, 5, 8);
    const trunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B4513, // Brown
      roughness: 0.9,
      metalness: 0.1,
    });
    const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
    trunk.position.y = 2.5;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    pineTree.add(trunk);
    
    // Tree foliage (multiple cones stacked)
    const foliageMaterial = new THREE.MeshStandardMaterial({
      color: 0x2E8B57, // Dark green
      roughness: 0.8,
      metalness: 0.1,
    });
    
    // Bottom layer (largest)
    const foliageGeo1 = new THREE.ConeGeometry(3, 4, 8);
    const foliage1 = new THREE.Mesh(foliageGeo1, foliageMaterial);
    foliage1.position.y = 3;
    foliage1.castShadow = true;
    foliage1.receiveShadow = true;
    pineTree.add(foliage1);
    
    // Middle layer
    const foliageGeo2 = new THREE.ConeGeometry(2.2, 3.5, 8);
    const foliage2 = new THREE.Mesh(foliageGeo2, foliageMaterial);
    foliage2.position.y = 5.5;
    foliage2.castShadow = true;
    foliage2.receiveShadow = true;
    pineTree.add(foliage2);
    
    // Top layer (smallest)
    const foliageGeo3 = new THREE.ConeGeometry(1.5, 3, 8);
    const foliage3 = new THREE.Mesh(foliageGeo3, foliageMaterial);
    foliage3.position.y = 7.5;
    foliage3.castShadow = true;
    foliage3.receiveShadow = true;
    pineTree.add(foliage3);
    
    // Oak tree model
    const oakTree = new THREE.Group();
    
    // Tree trunk
    const oakTrunkGeometry = new THREE.CylinderGeometry(0.8, 1.2, 6, 8);
    const oakTrunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B4513, // Brown
      roughness: 0.9,
      metalness: 0.1,
    });
    const oakTrunk = new THREE.Mesh(oakTrunkGeometry, oakTrunkMaterial);
    oakTrunk.position.y = 3;
    oakTrunk.castShadow = true;
    oakTrunk.receiveShadow = true;
    oakTree.add(oakTrunk);
    
    // Tree foliage (sphere)
    const oakFoliageMaterial = new THREE.MeshStandardMaterial({
      color: 0x228B22, // Forest green
      roughness: 0.8,
      metalness: 0.1,
    });
    
    // Main foliage
    const oakFoliageGeo = new THREE.SphereGeometry(4, 10, 10);
    const oakFoliage = new THREE.Mesh(oakFoliageGeo, oakFoliageMaterial);
    oakFoliage.position.y = 8;
    oakFoliage.scale.y = 0.8;
    oakFoliage.castShadow = true;
    oakFoliage.receiveShadow = true;
    oakTree.add(oakFoliage);
    
    // Simple tree model
    const simpleTree = new THREE.Group();
    
    // Tree trunk
    const simpleTrunkGeometry = new THREE.CylinderGeometry(0.4, 0.6, 4, 8);
    const simpleTrunkMaterial = new THREE.MeshStandardMaterial({
      color: 0x8B4513, // Brown
      roughness: 0.9,
      metalness: 0.1,
    });
    const simpleTrunk = new THREE.Mesh(simpleTrunkGeometry, simpleTrunkMaterial);
    simpleTrunk.position.y = 2;
    simpleTrunk.castShadow = true;
    simpleTrunk.receiveShadow = true;
    simpleTree.add(simpleTrunk);
    
    // Tree foliage (cone)
    const simpleFoliageMaterial = new THREE.MeshStandardMaterial({
      color: 0x3CB371, // Medium sea green
      roughness: 0.8,
      metalness: 0.1,
    });
    
    // Foliage
    const simpleFoliageGeo = new THREE.ConeGeometry(2.5, 5, 8);
    const simpleFoliage = new THREE.Mesh(simpleFoliageGeo, simpleFoliageMaterial);
    simpleFoliage.position.y = 5.5;
    simpleFoliage.castShadow = true;
    simpleFoliage.receiveShadow = true;
    simpleTree.add(simpleFoliage);
    
    // Add tree models to array
    this.treeModels.push(pineTree, oakTree, simpleTree);
  }
  
  /**
   * Creates different bush models for variety
   */
  private createBushModels(): void {
    // Create several different bush models for variety
    
    // Round bush
    const roundBush = new THREE.Group();
    const roundBushGeo = new THREE.SphereGeometry(1.5, 8, 8);
    const roundBushMaterial = new THREE.MeshStandardMaterial({
      color: 0x228B22, // Forest green
      roughness: 0.9,
      metalness: 0.1,
    });
    const roundBushMesh = new THREE.Mesh(roundBushGeo, roundBushMaterial);
    roundBushMesh.position.y = 1.5;
    roundBushMesh.castShadow = true;
    roundBushMesh.receiveShadow = true;
    roundBush.add(roundBushMesh);
    
    // Oval bush
    const ovalBush = new THREE.Group();
    const ovalBushGeo = new THREE.SphereGeometry(1.2, 8, 8);
    const ovalBushMaterial = new THREE.MeshStandardMaterial({
      color: 0x32CD32, // Lime green
      roughness: 0.9,
      metalness: 0.1,
    });
    const ovalBushMesh = new THREE.Mesh(ovalBushGeo, ovalBushMaterial);
    ovalBushMesh.position.y = 1.2;
    ovalBushMesh.scale.set(1.5, 1, 1.5);
    ovalBushMesh.castShadow = true;
    ovalBushMesh.receiveShadow = true;
    ovalBush.add(ovalBushMesh);
    
    // Small bush
    const smallBush = new THREE.Group();
    const smallBushGeo = new THREE.SphereGeometry(0.8, 8, 8);
    const smallBushMaterial = new THREE.MeshStandardMaterial({
      color: 0x006400, // Dark green
      roughness: 0.9,
      metalness: 0.1,
    });
    const smallBushMesh = new THREE.Mesh(smallBushGeo, smallBushMaterial);
    smallBushMesh.position.y = 0.8;
    smallBushMesh.castShadow = true;
    smallBushMesh.receiveShadow = true;
    smallBush.add(smallBushMesh);
    
    // Add bush models to array
    this.bushModels.push(roundBush, ovalBush, smallBush);
  }
  
  /**
   * Checks if a position is too close to the castle
   * @param x X coordinate
   * @param z Z coordinate
   * @returns True if the position is too close to the castle
   */
  private isTooCloseTocastle(x: number, z: number): boolean {
    // Castle is assumed to be at the center (0,0)
    const distanceToCenter = Math.sqrt(x * x + z * z);
    return distanceToCenter < this.castleRadius;
  }
  
  /**
   * Generates vegetation and adds it to the scene
   */
  private generateVegetation(): void {
    // Generate trees
    for (let i = 0; i < this.treeCount; i++) {
      // Choose a random tree model
      const modelIndex = Math.floor(Math.random() * this.treeModels.length);
      const treeModel = this.treeModels[modelIndex].clone();
      
      // Find a valid position (not too close to the castle)
      let x, z;
      
      // 30% chance to place trees in clusters near the castle perimeter
      if (Math.random() < 0.3) {
        // Place near the castle perimeter
        const angle = Math.random() * Math.PI * 2;
        const distance = this.castleRadius + 5 + Math.random() * 15;
        x = Math.cos(angle) * distance;
        z = Math.sin(angle) * distance;
      } else {
        // Place randomly in the world
        do {
          x = (Math.random() - 0.5) * this.boundarySize * 2;
          z = (Math.random() - 0.5) * this.boundarySize * 2;
        } while (this.isTooCloseTocastle(x, z));
      }
      
      // Position the tree
      treeModel.position.set(x, 0, z);
      
      // Random rotation
      treeModel.rotation.y = Math.random() * Math.PI * 2;
      
      // Random scale (make some trees bigger than others)
      const scale = 0.8 + Math.random() * 1.2;
      treeModel.scale.set(scale, scale, scale);
      
      // Add to scene and array
      this.scene.add(treeModel);
      this.trees.push(treeModel);
    }
    
    // Generate bushes
    for (let i = 0; i < this.bushCount; i++) {
      // Choose a random bush model
      const modelIndex = Math.floor(Math.random() * this.bushModels.length);
      const bushModel = this.bushModels[modelIndex].clone();
      
      // Find a valid position (not too close to the castle)
      let x, z;
      
      // 40% chance to place bushes in clusters near the castle perimeter
      if (Math.random() < 0.4) {
        // Place near the castle perimeter
        const angle = Math.random() * Math.PI * 2;
        const distance = this.castleRadius + 2 + Math.random() * 10;
        x = Math.cos(angle) * distance;
        z = Math.sin(angle) * distance;
      } else {
        // Place randomly in the world
        do {
          x = (Math.random() - 0.5) * this.boundarySize * 2;
          z = (Math.random() - 0.5) * this.boundarySize * 2;
        } while (this.isTooCloseTocastle(x, z));
      }
      
      // Position the bush
      bushModel.position.set(x, 0, z);
      
      // Random rotation
      bushModel.rotation.y = Math.random() * Math.PI * 2;
      
      // Random scale (make some bushes bigger than others)
      const scale = 0.7 + Math.random() * 1.3;
      bushModel.scale.set(scale, scale, scale);
      
      // Add to scene and array
      this.scene.add(bushModel);
      this.bushes.push(bushModel);
    }
  }
  
  /**
   * Updates vegetation (if needed)
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    // Currently no update needed for static vegetation
    // Could be used for wind effects or seasonal changes in the future
  }
  
  /**
   * Disposes of vegetation resources
   */
  public dispose(): void {
    // Remove trees from scene and dispose of resources
    for (const tree of this.trees) {
      this.scene.remove(tree);
      tree.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          } else if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          }
        }
      });
    }
    
    // Remove bushes from scene and dispose of resources
    for (const bush of this.bushes) {
      this.scene.remove(bush);
      bush.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          } else if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          }
        }
      });
    }
    
    // Clear arrays
    this.trees = [];
    this.bushes = [];
    this.treeModels = [];
    this.bushModels = [];
  }
} 